import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Dropdown,
  Form,
  Nav,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  AlertTriangle,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  Lock,
  Play,
  RefreshCw,
  XCircle,
} from "react-feather";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Link } from "react-router";
import { useGet, useGetLazy } from "../../api";
import { useApi } from "../../hooks";
import { settingsType, useSettings } from "../../hooks/settings";
import { ReportStatCard } from "../../components/stat-card";
import {
  ISessionRunDailyReportEntry,
  ISessionRunDailyReportResponse,
  ISessionRunListResponse,
  ISessionRunResult,
  ISessionRunStatusSummary,
  SESSION_RUN_REPORT_MAX_DAYS,
  TSessionRunPartStatus,
  TSessionRunStatus,
} from "../../interfaces/auto-coupons";
import {
  failureReasonBadge,
  failureReasonLabel,
} from "../../interfaces/failure-reasons";

const PAGE_SIZE = 20;
const POLL_MS = 10_000;

/** All possible run status values. */
const ALL_RUN_STATUSES: string[] = [
  "pass",
  "coupons_failed",
  "template_failed",
  "session_failed",
  "no_coupons",
  "no_template",
];

/**
 * The default status filter for the Live tab: everything except the two
 * informational-only statuses. This is a fixed UX default and is intentionally
 * decoupled from the Reporting tab's exclusion setting.
 */
const DEFAULT_LIVE_STATUS_FILTER = ALL_RUN_STATUSES.filter(
  (s) => s !== "no_coupons" && s !== "no_template"
);

type AppliedFilters = {
  domain: string;
  status: string[];
  skuTemplateStatus: string[];
  autoTemplateStatus: string[];
  couponsStatus: string[];
  dateFrom: string;
  dateTo: string;
};

/** Builds the list request URL from explicit args — no state diffing needed. */
const buildRunsUrl = (p: number, filters: AppliedFilters): string => {
  const qp = new URLSearchParams({ page: String(p), limit: String(PAGE_SIZE) });
  if (filters.domain) qp.set("domain", filters.domain);
  if (filters.status.length) qp.set("status", filters.status.join(","));

  if (filters.skuTemplateStatus.length)
    qp.set("skuTemplateStatus", filters.skuTemplateStatus.join(","));
  if (filters.autoTemplateStatus.length)
    qp.set("autoTemplateStatus", filters.autoTemplateStatus.join(","));
  if (filters.couponsStatus.length)
    qp.set("couponsStatus", filters.couponsStatus.join(","));
  if (filters.dateFrom)
    qp.set("dateFrom", new Date(`${filters.dateFrom}T00:00:00`).toISOString());
  if (filters.dateTo)
    qp.set("dateTo", new Date(`${filters.dateTo}T23:59:59.999`).toISOString());
  return `/auto-coupons/session-runs?${qp}`;
};

/** Compact "3m", "45s", "2h" from a millisecond duration. */
const formatAge = (ms: number | null): string => {
  if (ms == null) return "—";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

/** Local yyyy-mm-dd (toISOString would shift the day near midnight). */
const localDateStr = (d = new Date()): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const STATUS_META: Record<
  TSessionRunStatus,
  { label: string; bg: string; text?: string }
> = {
  pass: { label: "Pass", bg: "success" },
  coupons_failed: { label: "Coupons failed", bg: "warning", text: "dark" },
  template_failed: { label: "Template failed", bg: "danger" },
  session_failed: { label: "Session failed", bg: "secondary" },
  no_coupons: { label: "No coupons", bg: "light", text: "dark" },
  no_template: { label: "No template", bg: "light", text: "dark" },
};

const StatusBadge: React.FC<{ status: TSessionRunStatus }> = ({ status }) => {
  const meta = STATUS_META[status] ?? STATUS_META.session_failed;
  return (
    <Badge bg={meta.bg} text={meta.text as any}>
      {meta.label}
    </Badge>
  );
};

/**
 * The main status cell. While a run is in-flight (scheduled/running) or has
 * hard-failed, it shows the lifecycle state; once it completes it shows the
 * outcome verdict.
 */
const RunCell: React.FC<{ run: ISessionRunResult }> = ({ run }) => {
  if (run.runState === "scheduled")
    return (
      <Badge bg="light" text="dark" title="Enqueued — waiting for a runner">
        <Clock size={11} className="me-1" />
        Scheduled
      </Badge>
    );
  if (run.runState === "running")
    return (
      <Badge bg="info" text="dark">
        <Spinner
          animation="border"
          size="sm"
          className="me-1"
          style={{ width: 10, height: 10, borderWidth: 2 }}
        />
        Running
      </Badge>
    );
  if (run.runState === "failed")
    return (
      <Badge
        bg="danger"
        title={run.errorMessage ?? "Run failed before it completed"}
      >
        <AlertTriangle size={11} className="me-1" />
        Failed
      </Badge>
    );
  return run.status ? (
    <StatusBadge status={run.status} />
  ) : (
    <Badge bg="secondary">—</Badge>
  );
};

/** One stat in the live summary header. */
const StatCard: React.FC<{
  label: string;
  value: number;
  variant?: string;
  icon?: React.ReactNode;
  hint?: string;
  warn?: boolean;
}> = ({ label, value, variant = "secondary", icon, hint, warn }) => (
  <Col>
    <Card className={warn ? "border-warning" : ""}>
      <Card.Body className="py-2 px-3">
        <div className="d-flex align-items-center justify-content-between">
          <span className="small text-muted text-nowrap">
            {icon}
            {label}
          </span>
          <span className={`fw-bold fs-5 text-${warn ? "warning" : variant}`}>
            {value}
          </span>
        </div>
        {hint && <div className="small text-warning">{hint}</div>}
      </Card.Body>
    </Card>
  </Col>
);

/** Live runner summary shown at the top of the page. */
const SummaryBar: React.FC<{
  summary?: ISessionRunStatusSummary | null;
}> = ({ summary }) => {
  if (!summary) return null;
  // Merchants are due but nothing is running — the "not getting picked up"
  // signal that motivated this view.
  const stalled = summary.merchants.dueNow > 0 && summary.running === 0;

  return (
    <>
      <Row className="g-2 mb-2" xs={2} md={6}>
        <StatCard
          label="Pending"
          value={summary.pending}
          variant="dark"
          icon={<Clock size={13} className="me-1" />}
        />
        <StatCard
          label="Running"
          value={summary.running}
          variant="info"
          icon={<Play size={13} className="me-1" />}
        />
        <StatCard
          label="Due now"
          value={summary.merchants.dueNow}
          warn={stalled}
          icon={<AlertTriangle size={13} className="me-1" />}
          hint={stalled ? "Not picked up" : undefined}
        />
        <StatCard
          label="Locked"
          value={summary.merchants.locked}
          icon={<Lock size={13} className="me-1" />}
        />
        <StatCard
          label="Done today"
          value={summary.completedToday}
          variant="success"
          icon={<CheckCircle size={13} className="me-1" />}
        />
        <StatCard
          label="Failed today"
          value={summary.failedToday}
          variant="danger"
          icon={<XCircle size={13} className="me-1" />}
        />
      </Row>

      {stalled && (
        <Alert variant="warning" className="py-2 small mb-2">
          <AlertTriangle size={14} className="me-1" />
          {summary.merchants.dueNow} session merchant
          {summary.merchants.dueNow === 1 ? " is" : "s are"} due but nothing is
          running — a runner may not be claiming them.
        </Alert>
      )}

      {summary.runningRuns.length > 0 && (
        <div className="small text-muted mb-3">
          <span className="fw-semibold">Running now: </span>
          {summary.runningRuns
            .map((r) => `${r.domain} (${formatAge(r.ageMs)})`)
            .join(", ")}
        </div>
      )}
    </>
  );
};

/** pass / fail / not_tested badge for one part of the run. */
const PartBadge: React.FC<{
  status: TSessionRunPartStatus;
  notTestedHint: string;
}> = ({ status, notTestedHint }) => {
  if (status === "not_tested")
    return (
      <Badge bg="light" text="dark" title={notTestedHint}>
        not tested
      </Badge>
    );
  return status === "pass" ? (
    <Badge bg="success">pass</Badge>
  ) : (
    <Badge bg="danger">fail</Badge>
  );
};

const STATUS_OPTIONS = [
  { value: "pass", label: "Pass" },
  { value: "coupons_failed", label: "Coupons failed" },
  { value: "template_failed", label: "Template failed" },
  { value: "session_failed", label: "Session failed" },
  { value: "no_coupons", label: "No coupons" },
  { value: "no_template", label: "No template" },
];

const PART_OPTIONS = [
  { value: "pass", label: "Pass" },
  { value: "fail", label: "Fail" },
  { value: "not_tested", label: "Not tested" },
];

/** Checkbox dropdown — any combination of values (e.g. Fail + Not tested). */
const MultiSelectFilter: React.FC<{
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
}> = ({ options, selected, onChange }) => {
  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );

  const summary =
    selected.length === 0
      ? "All"
      : options
          .filter((o) => selected.includes(o.value))
          .map((o) => o.label)
          .join(", ");

  return (
    <Dropdown autoClose="outside">
      <Dropdown.Toggle
        size="sm"
        variant="outline-secondary"
        className="w-100 text-start text-truncate"
      >
        {summary}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {options.map((o) => (
          <Dropdown.Item
            key={o.value}
            onClick={(e) => {
              e.preventDefault();
              toggle(o.value);
            }}
          >
            <span className="me-2 d-inline-block" style={{ width: 16 }}>
              {selected.includes(o.value) && <Check size={14} />}
            </span>
            {o.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

/** Expanded detail: what exactly failed — session, templates, or coupons. */
const RunDetail: React.FC<{ run: ISessionRunResult }> = ({ run }) => (
  <div className="p-3 bg-light">
    {run.runState === "scheduled" && (
      <Alert variant="secondary" className="py-2 mb-3">
        <Clock size={14} className="me-1" />
        Enqueued and waiting for a runner to pick it up.
      </Alert>
    )}
    {run.runState === "running" && (
      <Alert variant="info" className="py-2 mb-3">
        Run in progress…
      </Alert>
    )}
    {run.runState === "failed" && !run.status && (
      <Alert variant="danger" className="py-2 mb-3">
        <strong>Run failed before it completed</strong>
        {run.errorMessage && (
          <div className="small mt-1">{run.errorMessage}</div>
        )}
      </Alert>
    )}
    {run.status && run.status !== "pass" && (
      <Alert
        variant={run.status === "coupons_failed" ? "warning" : "danger"}
        className="py-2 mb-3"
      >
        <strong>{STATUS_META[run.status].label}</strong>
        {run.failureReason && (
          <Badge
            bg={failureReasonBadge(run.failureReason).bg}
            text={failureReasonBadge(run.failureReason).text as any}
            className="ms-2"
          >
            {failureReasonLabel(run.failureReason)}
          </Badge>
        )}
        {run.errorMessage && (
          <div className="small mt-1">{run.errorMessage}</div>
        )}
      </Alert>
    )}

    {(run.autoFailureReason || run.skuFailureReason) && (
      <div className="small mb-3">
        {run.autoFailureReason && (
          <div>
            <span className="text-muted">Auto template:</span>{" "}
            <span className="text-danger">
              {failureReasonLabel(run.autoFailureReason)}
            </span>
          </div>
        )}
        {run.skuFailureReason && (
          <div>
            <span className="text-muted">SKU template:</span>{" "}
            <span className="text-danger">{run.skuFailureReason}</span>
          </div>
        )}
      </div>
    )}

    {run.reviewerVerdict && (
      <div className="mb-3 p-2 border rounded bg-white">
        <h6 className="small fw-semibold mb-2">QA reviewer verdict</h6>
        <div className="d-flex gap-4 small">
          <div>
            <span className="text-muted me-1">Auto template:</span>
            <PartBadge
              status={run.reviewerVerdict.autoTemplateStatus}
              notTestedHint="Reviewer did not rate the auto template"
            />
            {run.reviewerVerdict.autoFailureReason && (
              <div className="text-danger mt-1">
                {run.reviewerVerdict.autoFailureReason}
              </div>
            )}
          </div>
          <div>
            <span className="text-muted me-1">SKU template:</span>
            <PartBadge
              status={run.reviewerVerdict.skuTemplateStatus}
              notTestedHint="Reviewer did not rate the SKU template"
            />
            {run.reviewerVerdict.skuFailureReason && (
              <div className="text-danger mt-1">
                {run.reviewerVerdict.skuFailureReason}
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    <Row>
      <Col md={7}>
        <h6 className="small fw-semibold">
          Coupon codes ({run.codes.length})
          {run.originalTotal != null &&
            ` — basket total ${run.currency ?? ""}${run.originalTotal}`}
        </h6>
        {run.codes.length === 0 ? (
          <div className="small text-muted mb-3">
            No codes were tested — the run ended before the coupon loop.
          </div>
        ) : (
          <Table size="sm" bordered className="bg-white">
            <thead>
              <tr>
                <th>Code</th>
                <th>Result</th>
                <th>Savings</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {run.codes.map((code, i) => (
                <tr key={`${code.code}-${i}`}>
                  <td className="font-monospace">{code.code}</td>
                  <td>
                    {code.notApplicableToBasket ? (
                      <Badge bg="info">Not Applicable</Badge>
                    ) : code.success ? (
                      <Badge bg="success">Applied</Badge>
                    ) : (
                      <Badge bg="danger">Failed</Badge>
                    )}
                  </td>
                  <td>
                    {code.success && code.savings > 0
                      ? `${run.currency ?? ""}${code.savings.toFixed(2)}`
                      : "—"}
                  </td>
                  <td className="small text-muted">
                    {code.errorMessage || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Col>

      <Col md={5}>
        <h6 className="small fw-semibold">
          Captured basket ({run.capturedItems.length} item
          {run.capturedItems.length === 1 ? "" : "s"})
          {run.skuTemplateStatus === "fail" && (
            <span className="text-danger ms-1">— SKU template failing?</span>
          )}
        </h6>
        {run.capturedItems.length === 0 ? (
          <div className="small text-muted mb-3">
            {run.skuTemplateStatus === "not_tested"
              ? "SKU capture not tested (no live SKU template, or the run failed earlier)."
              : "Nothing captured — check the SKU selectors."}
          </div>
        ) : (
          <Table size="sm" bordered className="bg-white">
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              {run.capturedItems.map((item, i) => (
                <tr key={i}>
                  <td className="small">{item.name}</td>
                  <td>{item.quantity}</td>
                  <td>{item.price != null ? item.price : "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <div className="small text-muted">
          <div>
            Template: {run.autoTemplateSource ?? "?"}{" "}
            {run.autoTemplateVersion != null && `v${run.autoTemplateVersion}`} (
            {run.templateView ?? "?"})
            {run.aiCalled && (
              <Badge bg="primary" className="ms-1">
                AI called
              </Badge>
            )}
          </div>
          {run.sessionAgeMs != null && (
            <div>
              Session age at replay:{" "}
              {Math.round(run.sessionAgeMs / (60 * 60 * 1000))}h
            </div>
          )}
          <div className="mt-1 d-flex gap-3">
            <Link
              to={`/auto-coupons/runs?correlationId=${encodeURIComponent(
                run.correlationId
              )}`}
              className="text-decoration-none fw-semibold"
              title="Open this run to see its full logs — useful when a run failed before producing an evaluation"
            >
              Run logs →
            </Link>
            {run.evaluationId && (
              <Link
                to={`/auto-coupons/evaluations?correlationId=${run.correlationId}`}
                className="text-decoration-none"
              >
                Evaluation →
              </Link>
            )}
            <Link to={`/tasks/${run.taskId}`} className="text-decoration-none">
              Task →
            </Link>
          </div>
        </div>
      </Col>
    </Row>
  </div>
);

// ── Reporting tab (daily verdict-status analytics) ─────────────────────────

/** Chart series — one line per verdict status, plus the running total. */
const REPORT_LINES: Array<{
  key: keyof ISessionRunDailyReportEntry;
  label: string;
  color: string;
}> = [
  { key: "total", label: "Total", color: "#6c757d" },
  { key: "pass", label: "Pass", color: "#198754" },
  { key: "couponsFailed", label: "Coupons failed", color: "#fd7e14" },
  { key: "templateFailed", label: "Template failed", color: "#dc3545" },
  { key: "sessionFailed", label: "Session failed", color: "#6f42c1" },
  { key: "noCoupons", label: "No coupons", color: "#0dcaf0" },
  { key: "noTemplate", label: "No template", color: "#adb5bd" },
];

const DAY_RANGE_OPTIONS = [7, 14, SESSION_RUN_REPORT_MAX_DAYS];

/** Maps a TSessionRunStatus key to the corresponding ISessionRunDailyReportEntry key. */
const STATUS_TO_REPORT_KEY: Record<string, string> = {
  pass: "pass",
  coupons_failed: "couponsFailed",
  template_failed: "templateFailed",
  session_failed: "sessionFailed",
  no_coupons: "noCoupons",
  no_template: "noTemplate",
};

const ReportingTab: React.FC<{
  excludedStatuses: string[];
  setExcludedStatuses: (next: string[]) => void;
}> = ({ excludedStatuses, setExcludedStatuses }) => {
  const [days, setDays] = useState(SESSION_RUN_REPORT_MAX_DAYS);

  // Derive excluded report keys from the exclusion list.
  const excludedReportKeys = excludedStatuses
    .map((s) => STATUS_TO_REPORT_KEY[s])
    .filter(Boolean);

  // Default to the failure lines + pass, minus excluded statuses.
  // Total is often much larger and would flatten the others, so it starts hidden.
  const [visibleLines, setVisibleLines] = useState<Set<string>>(
    () =>
      new Set(
        [
          "pass",
          "couponsFailed",
          "templateFailed",
          "sessionFailed",
          "noCoupons",
          "noTemplate",
        ].filter((k) => !excludedReportKeys.includes(k))
      )
  );
  const [downloading, setDownloading] = useState<"daily" | "domain" | null>(
    null
  );
  const [refreshing, setRefreshing] = useState(false);

  const excludeParam =
    excludedStatuses.length > 0 ? `&exclude=${excludedStatuses.join(",")}` : "";
  const { data, loading, error, refetch } =
    useGet<ISessionRunDailyReportResponse>(
      `/auto-coupons/session-runs/report?days=${days}${excludeParam}`
    );

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await refetch();
    } catch {
      // Surfaced via the `error` state below.
    } finally {
      setRefreshing(false);
    }
  };
  const { execute: fetchCsv } = useGetLazy<string>();

  const entries = data?.entries ?? [];
  const latest = entries.length ? entries[entries.length - 1] : null;

  // Window totals give the "today" cards some context.
  const windowTotals = entries.reduce(
    (acc, e) => {
      acc.total += e.total;
      acc.pass += e.pass;
      return acc;
    },
    { total: 0, pass: 0 }
  );
  const windowPassRate =
    windowTotals.total > 0 ? windowTotals.pass / windowTotals.total : 0;

  // How many runs fall under hidden statuses for a given entry.
  const calcExcludedCount = useCallback(
    (e: ISessionRunDailyReportEntry) =>
      (excludedStatuses.includes("no_coupons") ? e.noCoupons : 0) +
      (excludedStatuses.includes("no_template") ? e.noTemplate : 0) +
      (excludedStatuses.includes("coupons_failed") ? e.couponsFailed : 0) +
      (excludedStatuses.includes("template_failed") ? e.templateFailed : 0) +
      (excludedStatuses.includes("session_failed") ? e.sessionFailed : 0),
    [excludedStatuses]
  );

  // Adjusted (visible) total for today.
  const latestRawTotal = latest?.total ?? 0;
  const latestExcludedCount = latest ? calcExcludedCount(latest) : 0;
  const latestAdjustedTotal = latestRawTotal - latestExcludedCount;
  const totalRunsValue =
    latestExcludedCount > 0
      ? `${latestAdjustedTotal} (${latestRawTotal})`
      : latestRawTotal;

  const toggleLine = useCallback((key: string) => {
    setVisibleLines((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const download = async (mode: "daily" | "domain") => {
    setDownloading(mode);
    try {
      const csv = await fetchCsv(
        `/auto-coupons/session-runs/report/export?days=${days}&mode=${mode}`,
        { responseType: "text" }
      );
      if (!csv) return;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = latest?.date ?? new Date().toISOString().slice(0, 10);
      a.download = `session-runs-report-${
        mode === "domain" ? "by-domain-geo" : "daily"
      }-${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(null);
    }
  };

  const pctLabel = (r: number) => `${Math.round(r * 100)}%`;

  return (
    <div>
      {/* Controls */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <span className="small text-muted">Window:</span>
          <ButtonGroup size="sm">
            {DAY_RANGE_OPTIONS.map((d) => (
              <Button
                key={d}
                variant={d === days ? "primary" : "outline-secondary"}
                onClick={() => setDays(d)}
              >
                {d}d
              </Button>
            ))}
          </ButtonGroup>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <RefreshCw size={14} className="me-1" />
            )}
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
          <Dropdown as={ButtonGroup} size="sm">
            <Button
              variant="outline-success"
              onClick={() => download("daily")}
              disabled={!!downloading || entries.length === 0}
              title="Export one row per day (summary)"
            >
              {downloading === "daily" ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <Download size={14} className="me-1" />
              )}
              CSV: summary by day
            </Button>
            <Dropdown.Toggle
              split
              variant="outline-success"
              disabled={!!downloading || entries.length === 0}
            />
            <Dropdown.Menu align="end">
              <Dropdown.Item
                onClick={() => download("domain")}
                disabled={!!downloading}
                title="Export one row per day / domain / country"
              >
                {downloading === "domain" ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : (
                  <Download size={13} className="me-2" />
                )}
                CSV: by domain / geo
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {loading && !data && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}
      {error && <Alert variant="danger">Failed to load report.</Alert>}

      {!error && data && (
        <>
          {/* ── Exclusion list ── */}
          <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
            <span className="small text-muted">Hidden statuses:</span>
            {STATUS_OPTIONS.map((o) => (
              <Form.Check
                key={o.value}
                type="checkbox"
                id={`report-exclude-${o.value}`}
                label={o.label}
                className="small"
                checked={excludedStatuses.includes(o.value)}
                onChange={() => {
                  const next = excludedStatuses.includes(o.value)
                    ? excludedStatuses.filter((s) => s !== o.value)
                    : [...excludedStatuses, o.value];
                  setExcludedStatuses(next);
                  // Sync chart visibility — remove or restore the corresponding line.
                  const reportKey = STATUS_TO_REPORT_KEY[o.value];
                  if (reportKey) {
                    setVisibleLines((prev) => {
                      const updated = new Set(prev);
                      if (next.includes(o.value)) updated.delete(reportKey);
                      else updated.add(reportKey);
                      return updated;
                    });
                  }
                }}
              />
            ))}
          </div>

          {/* ── Today so far (UTC) ── */}
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
            <h6 className="mb-0 text-muted">
              Today so far (UTC):{" "}
              <strong className="text-dark">{latest?.date ?? "—"}</strong>
            </h6>
            <span className="small text-muted">
              Last {days}d: {windowTotals.total} run
              {windowTotals.total === 1 ? "" : "s"} · {pctLabel(windowPassRate)}{" "}
              pass
            </span>
          </div>

          <Row className="g-2 mb-4">
            <ReportStatCard
              label="Total runs"
              value={totalRunsValue}
              color="dark"
              sub="Runs with a verdict today"
            />
            <ReportStatCard
              label="Pass"
              value={latest?.pass ?? 0}
              color="success"
            />
            <ReportStatCard
              label="Pass rate"
              value={latest ? pctLabel(latest.passRate) : "—"}
              color="success"
            />
            <ReportStatCard
              label="Coupons failed"
              value={latest?.couponsFailed ?? 0}
              color="warning"
            />
            <ReportStatCard
              label="Template failed"
              value={latest?.templateFailed ?? 0}
              color="danger"
            />
            <ReportStatCard
              label="Session failed"
              value={latest?.sessionFailed ?? 0}
              color="danger"
            />
            {!excludedStatuses.includes("no_coupons") && (
              <ReportStatCard
                label="No coupons"
                value={latest?.noCoupons ?? 0}
                color="info"
              />
            )}
            {!excludedStatuses.includes("no_template") && (
              <ReportStatCard
                label="No template"
                value={latest?.noTemplate ?? 0}
                color="secondary"
              />
            )}
          </Row>

          {/* ── Trend chart ── */}
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center py-2 flex-wrap gap-2">
              <span className="fw-semibold small">Daily trend ({days}d)</span>
              <div className="d-flex flex-wrap gap-2">
                {REPORT_LINES.filter(
                  (l) => !excludedReportKeys.includes(l.key as string)
                ).map((l) => (
                  <button
                    key={l.key}
                    onClick={() => toggleLine(l.key)}
                    className="border-0 bg-transparent p-0 small d-flex align-items-center gap-1"
                    style={{
                      opacity: visibleLines.has(l.key) ? 1 : 0.35,
                      cursor: "pointer",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: 12,
                        height: 3,
                        background: l.color,
                        borderRadius: 2,
                      }}
                    />
                    {l.label}
                  </button>
                ))}
              </div>
            </Card.Header>
            <Card.Body className="p-2">
              <ResponsiveContainer width="100%" height={320}>
                <LineChart
                  data={entries}
                  margin={{ top: 4, right: 16, bottom: 0, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => String(v).slice(5)} // MM-DD
                  />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ fontSize: 12 }}
                    labelFormatter={(l) => `Date: ${l}`}
                  />
                  {REPORT_LINES.filter((l) => visibleLines.has(l.key)).map(
                    (l) => (
                      <Line
                        key={l.key}
                        type="monotone"
                        dataKey={l.key}
                        name={l.label}
                        stroke={l.color}
                        strokeWidth={2}
                        dot={entries.length <= 31}
                        activeDot={{ r: 4 }}
                      />
                    )
                  )}
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>

          {/* ── Daily breakdown table ── */}
          <Card>
            <Card.Header className="py-2">
              <span className="fw-semibold small">
                Daily breakdown ({entries.length} days)
              </span>
            </Card.Header>
            <Card.Body className="p-0">
              <div style={{ maxHeight: 420, overflowY: "auto" }}>
                <Table size="sm" hover className="mb-0 small">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>Date (UTC)</th>
                      <th>Total</th>
                      <th>Pass</th>
                      <th>Coupons failed</th>
                      <th>Template failed</th>
                      <th>Session failed</th>
                      {!excludedStatuses.includes("no_coupons") && (
                        <th>No coupons</th>
                      )}
                      {!excludedStatuses.includes("no_template") && (
                        <th>No template</th>
                      )}
                      <th>Pass rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...entries].reverse().map((e) => (
                      <tr key={e.date}>
                        <td className="fw-semibold">{e.date}</td>
                        <td>
                          {(() => {
                            const excl = calcExcludedCount(e);
                            return excl > 0
                              ? `${e.total - excl} (${e.total})`
                              : e.total;
                          })()}
                        </td>
                        <td>
                          {e.pass > 0 ? (
                            <Badge bg="success">{e.pass}</Badge>
                          ) : (
                            0
                          )}
                        </td>
                        <td>
                          {e.couponsFailed > 0 ? (
                            <Badge bg="warning" text="dark">
                              {e.couponsFailed}
                            </Badge>
                          ) : (
                            0
                          )}
                        </td>
                        <td>
                          {e.templateFailed > 0 ? (
                            <Badge bg="danger">{e.templateFailed}</Badge>
                          ) : (
                            0
                          )}
                        </td>
                        <td>
                          {e.sessionFailed > 0 ? (
                            <Badge bg="danger">{e.sessionFailed}</Badge>
                          ) : (
                            0
                          )}
                        </td>
                        {!excludedStatuses.includes("no_coupons") && (
                          <td>
                            {e.noCoupons > 0 ? (
                              <Badge bg="info">{e.noCoupons}</Badge>
                            ) : (
                              0
                            )}
                          </td>
                        )}
                        {!excludedStatuses.includes("no_template") && (
                          <td>
                            {e.noTemplate > 0 ? (
                              <Badge bg="secondary">{e.noTemplate}</Badge>
                            ) : (
                              0
                            )}
                          </td>
                        )}
                        <td>{e.total > 0 ? pctLabel(e.passRate) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};

export const AutoCouponSessionRuns: React.FC = () => {
  // Exclusion setting is used by the Reporting tab only.
  const [excludedStatusesSetting, setExcludedStatusesSetting] = useSettings(
    settingsType.SESSION_RUNS_EXCLUDED_STATUSES
  );
  const excludedStatuses = (excludedStatusesSetting ?? "no_coupons,no_template")
    .split(",")
    .filter(Boolean);
  const setExcludedStatuses = (next: string[]) =>
    setExcludedStatusesSetting(next.join(","));

  const [page, setPage] = useState(1);
  const [domainInput, setDomainInput] = useState("");
  const [statusInput, setStatusInput] = useState<string[]>(
    DEFAULT_LIVE_STATUS_FILTER
  );
  const [skuInput, setSkuInput] = useState<string[]>([]);
  const [templateInput, setTemplateInput] = useState<string[]>([]);
  const [couponsInput, setCouponsInput] = useState<string[]>([]);
  // Date range defaults to today — schedules run daily.
  const [dateFromInput, setDateFromInput] = useState(localDateStr());
  const [dateToInput, setDateToInput] = useState(localDateStr());
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({
    domain: "",
    status: DEFAULT_LIVE_STATUS_FILTER,
    skuTemplateStatus: [],
    autoTemplateStatus: [],
    couponsStatus: [],
    dateFrom: localDateStr(),
    dateTo: localDateStr(),
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rerunningId, setRerunningId] = useState<string | null>(null);
  const [view, setView] = useState<"live" | "reporting">("live");
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [searching, setSearching] = useState(false);
  const { get } = useApi();
  const { execute: fetchCsv } = useGetLazy<string>();

  const {
    data,
    loading,
    error,
    execute: fetchRuns,
  } = useGetLazy<ISessionRunListResponse>();

  // Stable ref so the auto-refresh interval always uses the current URL
  // without needing to be in its dependency array.
  const runsUrlRef = useRef("");
  runsUrlRef.current = buildRunsUrl(page, appliedFilters);

  // Initial fetch on mount.
  useEffect(() => {
    fetchRuns(runsUrlRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear the searching flag as soon as the triggered fetch resolves.
  useEffect(() => {
    if (!loading) setSearching(false);
  }, [loading]);

  const { data: summary, refetch: refetchSummary } =
    useGet<ISessionRunStatusSummary>("/auto-coupons/session-runs/status");

  const [autoRefresh, setAutoRefresh] = useState(false);

  // Poll the list + summary so runs visibly move scheduled → running → done.
  // Only while the live tab is showing — no point polling behind Reporting.
  useEffect(() => {
    if (!autoRefresh || view !== "live") return;
    const id = setInterval(() => {
      fetchRuns(runsUrlRef.current);
      refetchSummary();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [autoRefresh, view, fetchRuns, refetchSummary]);

  const runs = data?.sessionRuns ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const applyFilters = () => {
    const newFilters: AppliedFilters = {
      domain: domainInput,
      status: statusInput,
      skuTemplateStatus: skuInput,
      autoTemplateStatus: templateInput,
      couponsStatus: couponsInput,
      dateFrom: dateFromInput,
      dateTo: dateToInput,
    };
    setSearching(true);
    setAppliedFilters(newFilters);
    setPage(1);
    fetchRuns(buildRunsUrl(1, newFilters));
  };

  const clearFilters = () => {
    const cleared: AppliedFilters = {
      domain: "",
      status: DEFAULT_LIVE_STATUS_FILTER,
      skuTemplateStatus: [],
      autoTemplateStatus: [],
      couponsStatus: [],
      dateFrom: localDateStr(),
      dateTo: localDateStr(),
    };
    setDomainInput("");
    setStatusInput(DEFAULT_LIVE_STATUS_FILTER);
    setSkuInput([]);
    setTemplateInput([]);
    setCouponsInput([]);
    // Dates reset to the default (today), not to "all time".
    setDateFromInput(localDateStr());
    setDateToInput(localDateStr());
    setAppliedFilters(cleared);
    setPage(1);
    fetchRuns(buildRunsUrl(1, cleared));
  };

  const rerunSession = async (e: React.MouseEvent, run: ISessionRunResult) => {
    e.stopPropagation(); // don't toggle the row
    if (!run.merchantId) {
      window.alert("No merchant linked to this run.");
      return;
    }
    try {
      setRerunningId(run._id);
      const res = await get<{ message: string }>(
        `/test/session-run?merchantId=${run.merchantId}`
      );
      window.alert(res?.message ?? "Session run triggered");
    } catch (err: any) {
      window.alert(
        err?.response?.data?.message ?? "Failed to trigger session run"
      );
    } finally {
      setRerunningId(null);
    }
  };

  // Manual refresh fires both the list and the summary. We hold a local flag
  // for the whole round-trip (not the list's `loading`, which the 10s poll
  // also toggles) so the button stays disabled + spinning until both return —
  // stopping impatient repeat-clicks while the backend is still working.
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([
        fetchRuns(buildRunsUrl(page, appliedFilters)),
        refetchSummary(),
      ]);
    } catch {
      // Errors surface via the list's `error` state; nothing to do here.
    } finally {
      setRefreshing(false);
    }
  };

  // Export the currently-filtered runs (applied filters, all pages) as CSV.
  // Mirrors the query-param building used for the list request above.
  const downloadCsv = async () => {
    if (downloadingCsv) return;
    setDownloadingCsv(true);
    try {
      const exportParams = new URLSearchParams();
      if (appliedFilters.domain)
        exportParams.set("domain", appliedFilters.domain);
      if (appliedFilters.status.length)
        exportParams.set("status", appliedFilters.status.join(","));
      if (appliedFilters.skuTemplateStatus.length)
        exportParams.set(
          "skuTemplateStatus",
          appliedFilters.skuTemplateStatus.join(",")
        );
      if (appliedFilters.autoTemplateStatus.length)
        exportParams.set(
          "autoTemplateStatus",
          appliedFilters.autoTemplateStatus.join(",")
        );
      if (appliedFilters.couponsStatus.length)
        exportParams.set(
          "couponsStatus",
          appliedFilters.couponsStatus.join(",")
        );
      if (appliedFilters.dateFrom)
        exportParams.set(
          "dateFrom",
          new Date(`${appliedFilters.dateFrom}T00:00:00`).toISOString()
        );
      if (appliedFilters.dateTo)
        exportParams.set(
          "dateTo",
          new Date(`${appliedFilters.dateTo}T23:59:59.999`).toISOString()
        );

      const csv = await fetchCsv(
        `/auto-coupons/session-runs/export?${exportParams}`,
        { responseType: "text" }
      );
      if (!csv) return;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `session-runs-${localDateStr()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingCsv(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Session Runs</h4>
        {view === "live" && (
          <div className="d-flex align-items-center gap-3">
            <Form.Check
              type="switch"
              id="session-runs-auto-refresh"
              label="Auto-refresh"
              className="small"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <RefreshCw size={14} className="me-1" />
              )}
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
            <Button
              variant="outline-success"
              size="sm"
              onClick={downloadCsv}
              disabled={downloadingCsv || total === 0}
              title="Download the currently-filtered runs as CSV"
            >
              {downloadingCsv ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <Download size={14} className="me-1" />
              )}
              {downloadingCsv ? "Preparing…" : "CSV"}
            </Button>
          </div>
        )}
      </div>

      <Nav
        variant="tabs"
        activeKey={view}
        onSelect={(k) => setView((k ?? "live") as "live" | "reporting")}
        className="mb-3"
      >
        <Nav.Item>
          <Nav.Link eventKey="live">Live</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="reporting">Reporting</Nav.Link>
        </Nav.Item>
      </Nav>

      {view === "reporting" && (
        <ReportingTab
          excludedStatuses={excludedStatuses}
          setExcludedStatuses={setExcludedStatuses}
        />
      )}

      {view === "live" && (
        <>
          <SummaryBar summary={summary} />

          <Card className="mb-3">
            <Card.Body>
              <Row className="align-items-end g-2">
                <Col md={4}>
                  <Form.Label className="small">Domain</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="e.g. chelseacharles.com"
                    value={domainInput}
                    onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                    onChange={(e) => setDomainInput(e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Form.Label className="small">From</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={dateFromInput}
                    onChange={(e) => setDateFromInput(e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Form.Label className="small">To</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={dateToInput}
                    onChange={(e) => setDateToInput(e.target.value)}
                  />
                </Col>
                <Col md={4} className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={applyFilters}
                    disabled={searching}
                  >
                    {searching ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-1"
                        />
                        Searching…
                      </>
                    ) : (
                      "Search"
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={clearFilters}
                  >
                    Clear
                  </Button>
                </Col>
              </Row>
              <Row className="align-items-end g-2 mt-1">
                <Col md={3}>
                  <Form.Label className="small">Status</Form.Label>
                  <MultiSelectFilter
                    options={STATUS_OPTIONS}
                    selected={statusInput}
                    onChange={setStatusInput}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="small">SKU template</Form.Label>
                  <MultiSelectFilter
                    options={PART_OPTIONS}
                    selected={skuInput}
                    onChange={setSkuInput}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="small">Coupon template</Form.Label>
                  <MultiSelectFilter
                    options={PART_OPTIONS}
                    selected={templateInput}
                    onChange={setTemplateInput}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="small">Coupons</Form.Label>
                  <MultiSelectFilter
                    options={PART_OPTIONS}
                    selected={couponsInput}
                    onChange={setCouponsInput}
                  />
                </Col>
              </Row>
              {/* Exclusion list — persisted per device */}
            </Card.Body>
          </Card>

          {loading && !data && (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          )}
          {error && (
            <Alert variant="danger">Failed to load session runs.</Alert>
          )}

          {!error && data && (
            <>
              <Table hover responsive size="sm">
                <thead>
                  <tr>
                    <th style={{ width: 24 }}></th>
                    <th>Date</th>
                    <th>Domain</th>
                    <th>Country</th>
                    <th>Status</th>
                    <th>SKU template</th>
                    <th>Coupon template</th>
                    <th>Coupons</th>
                    <th title="Manual QA verdict from the linked evaluation">
                      QA (reviewer)
                    </th>
                    <th>Best saving</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {runs.length === 0 && (
                    <tr>
                      <td colSpan={11} className="text-center text-muted py-4">
                        No session runs found.
                      </td>
                    </tr>
                  )}
                  {runs.map((run) => (
                    <React.Fragment key={run._id}>
                      <tr
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          setExpandedId(expandedId === run._id ? null : run._id)
                        }
                      >
                        <td className="text-muted">
                          {expandedId === run._id ? (
                            <ChevronDown size={14} />
                          ) : (
                            <ChevronRight size={14} />
                          )}
                        </td>
                        <td className="small text-muted">
                          {new Date(run.createdAt).toLocaleString()}
                        </td>
                        <td>{run.domain}</td>
                        <td>{run.country ?? "—"}</td>
                        <td>
                          <RunCell run={run} />
                        </td>
                        <td>
                          <PartBadge
                            status={run.skuTemplateStatus}
                            notTestedHint="No live SKU template, or the run failed before capture"
                          />
                        </td>
                        <td>
                          <PartBadge
                            status={run.autoTemplateStatus}
                            notTestedHint="The run failed before the template could be exercised"
                          />
                        </td>
                        <td>
                          {run.couponsStatus === "not_tested" ? (
                            <Badge
                              bg="light"
                              text="dark"
                              title="No codes were tested"
                            >
                              not tested
                            </Badge>
                          ) : (
                            <span>
                              {run.successfulCoupons > 0 ? (
                                <CheckCircle
                                  size={12}
                                  className="me-1 text-success"
                                />
                              ) : (
                                <XCircle
                                  size={12}
                                  className="me-1 text-danger"
                                />
                              )}
                              {run.successfulCoupons}/{run.couponsEvaluated}
                            </span>
                          )}
                        </td>
                        <td>
                          {run.reviewerVerdict ? (
                            <div className="d-flex gap-1 align-items-center small">
                              <span className="text-muted">A</span>
                              <PartBadge
                                status={run.reviewerVerdict.autoTemplateStatus}
                                notTestedHint="Reviewer did not rate the auto template"
                              />
                              <span className="text-muted ms-1">S</span>
                              <PartBadge
                                status={run.reviewerVerdict.skuTemplateStatus}
                                notTestedHint="Reviewer did not rate the SKU template"
                              />
                            </div>
                          ) : (
                            <span
                              className="text-muted"
                              title="No manual QA verdict recorded for this run"
                            >
                              —
                            </span>
                          )}
                        </td>
                        <td>
                          {run.bestSavings > 0
                            ? `${run.currency ?? ""}${run.bestSavings.toFixed(2)}`
                            : "—"}
                        </td>
                        <td className="text-end">
                          <Button
                            size="sm"
                            variant="outline-success"
                            disabled={
                              rerunningId === run._id || !run.merchantId
                            }
                            onClick={(e) => rerunSession(e, run)}
                            title="Re-run this merchant's session test"
                          >
                            {rerunningId === run._id ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <Play size={13} />
                            )}
                          </Button>
                        </td>
                      </tr>
                      {expandedId === run._id && (
                        <tr>
                          <td colSpan={11} className="p-0">
                            <RunDetail run={run} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <small className="text-muted">{total} total</small>
                  <div className="d-flex gap-1">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page === 1}
                      onClick={() => {
                        setPage((p) => p - 1);
                        fetchRuns(buildRunsUrl(page - 1, appliedFilters));
                      }}
                    >
                      Prev
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page >= totalPages}
                      onClick={() => {
                        setPage((p) => p + 1);
                        fetchRuns(buildRunsUrl(page + 1, appliedFilters));
                      }}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
